import { BaseRepository } from "approot/global/repository/baseRepository";
import { AccountInfo } from "./sessionstore";
import { DialogsService, LongWorkService } from "approot/global/providers/uikit.dialogs";
import { EnumItemDto, FuncType, NamedEntityDto } from "approot/global/models/common.model";
import { EsiaPermissOptions } from "./sso-choose-role.component";

export interface AuthData {
	lt: string;
	ver: string;
	salt: string;
}

export interface LoginResult {
	at: string;
	accessToken?: string;
	refreshToken?: string;
	accountInfo?: AccountInfo;
	entryPoint: string;
	requestData?: any;
	code?: string;
}

export interface AuthUserInfo {
	id: string;
	displayName: string;
	userId: number;
	organizationId?: number;
	funcType?: number;
	emId?: number;
	binded: boolean;
	isStaff: boolean;
	isServAdmin: boolean;
	mustVerifyLoginPass: boolean;
	roles: NamedEntityDto[];
}

export interface IdpAccountInfo {
	trusted?: boolean;
	permissionGranted?: boolean;
	users: AuthUserInfo[];
}

export interface LoginViewData {
	productName: string;
	version: string;

	schoolLogin: boolean;
	emLogin: boolean;
	esiaLogin: boolean;
	esiaLoginPage: string;
	esiaMainAuth: boolean;
 
 
	esiaButton: boolean;
	signatureLogin: boolean;

	cacheVer: string;
	windowsAuth: boolean;

	enableSms: boolean;
	secureApi: boolean;

	esaLogin: boolean;
	esaLoginPage: string;

	languages: EnumItemDto[];
	defaultLanguage: string;
	loginPassAllowedFuncTypes: FuncType[];
}

export enum LoginType {
	ServAdmin = 0,
	School = 1,
	EducManager = 2,
	EducManagerForSchool = 3,
	Idp = 8,
	Refresh = 9
}

export interface IdpEnumItemDto {
	id: number,
	title: string,
	entryLinkName: string
}

export enum IdentityProvider {
	Irtech = 1,
	Esia = 2,
	Esa = 3
}

export enum LoginFormMode {
	educOrg = "EducOrg",
	em = "EducManagement"
}

export interface PrepareAuthFormResponse {
	// организация
	organization: NamedEntityDto;
}

export interface PrepareEmAuthFormResponse {
	// УО
	educManagement: NamedEntityDto;
}

export interface GetMobileInfoResponse {
	// признак МП
	isMobile: boolean;
	// признак привязки в МП
	mobileBind: boolean;
}

export class LoginRepository extends BaseRepository {
	/*@ngInject*/
	constructor($http, $dialogs: DialogsService, $longWork: LongWorkService, private $httpParamSerializer) {
		super($http, $dialogs, $longWork);
	}

	getFuncTypes() {
		return this.$http.get('/webapi/func-types')
		.then(this.handleResponse)
		.catch((err) => {
			return Promise.reject(err)
		})
	}

	getEsiaInfo(idpUserinfo) {
		return this.$http.post(`/webapi/sso/esia/idp-userinfo?loginState=${idpUserinfo}`)
			.then(this.handleResponse)
			.catch((err) => {
				return Promise.reject(err)
			})
	}

	getEsiaProviderInfo(idp, idpUserinfo, esiaPermissions?: boolean) {
		let requestUrl = `/webapi/sso/${idp}/idp-userinfo?loginState=${idpUserinfo}`;

		if (esiaPermissions) {
			requestUrl += "&esia_permissions=1";
		}

		return this.$http.post(requestUrl)
			.then(this.handleResponse)
			.catch((err) => {
				return Promise.reject(err)
			})
	}

	getClasses(loginState, orgId) {
		return this.$http.get(`/webapi/auth/classes?loginState=${loginState}&schoolId=${orgId}`)
			.then((response) => {
				return response.data
			})
			.catch((err) => {
				return Promise.reject(err)
			})
	}

	getAuthData(): Promise<AuthData> {
		return this.$http.post("/webapi/auth/getdata")
			.then(this.handleResponse, this.handleError)
	}

	sendReport(loginState, data, idp?: string) {
		return this.$http.post(`/webapi/sso/${idp??"esia"}/request-system-access?loginState=${loginState}`, data)
			.then(this.handleResponse, this.handleError)
	}

	getLoginViewData(): Promise<LoginViewData> {
		return this.$http.get("/webapi/logindata")
			.then(this.handleResponse, this.handleError);
	}

	auth(authParams): Promise<LoginResult> {
		let data = this.$httpParamSerializer(authParams);
		let options = {
			headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
		}
		return this.$http.post("/webapi/auth/login", data, options)
			.then(this.handleResponse, this.handleError)
	}

	logout(at: string): Promise<any> {
		return this.$http.post("/webapi/auth/logout", null, { headers: { at: at } })
			.then(this.handleResponse, this.handleError);
	}

	getAccountInfo(at: string): Promise<AccountInfo> {
		return this.$http.get("/webapi/auth/account", { headers: { at: at } })
			.then(this.handleResponse, this.handleError);
	}

	checkAccountList(list: AccountInfo[]): Promise<AccountInfo[]> {
		let data = { "accounts": list };

		return this.$http.post("/webapi/auth/accounts", data)
			.then(this.handleResponse, this.handleError);
	}

	getIdpUserList(idp: string, loginState: string): Promise<AuthUserInfo[]> {
		return this.$http.post(`/webapi/sso/${idp}/users`, { loginState })
			.then(this.handleResponse, this.handleError);
	}

	getIdpAccountInfo(idp: string, loginState: string): Promise<IdpAccountInfo> {
		return this.$http.get(`/webapi/sso/${idp}/account-info`, { params: { loginState: loginState } })
			.then(this.handleResponse, this.handleError);
	}

	getBindedUsers(ssoState: string): Promise<AuthUserInfo[]> {
		return this.$http.get(`/webapi/sso/binded-users`, { params: { ssoState } })
			.then(this.handleResponse, this.handleError);
	}

	getEducManagements(): Promise<NamedEntityDto[]> {
		return this.$http.get("/webapi/educmanagements")
			.then(this.handleResponse, this.handleError);
	}

	getIdpInfo(idp: string): Promise<IdpEnumItemDto> {
		return this.$http.get(`/webapi/sso/${idp}/info`)
			.then(this.handleResponse);
	}

	getPersonTrusted(idp: string, loginState: string): Promise<boolean> {
		return this.$http.get(`/webapi/sso/trusted`, { params: { loginState: loginState } })
			.then(this.handleResponse);
	}

	getIdpLogoutUrl(idp: string, loginState: string): Promise<string> {
		return this.$http.get(`/webapi/sso/${idp}/logout-url`, { params: { idp: idp , loginState: loginState } })
			.then(this.handleResponse);
	}

	checkGetAuthData(idp: string, loginState: string): Promise<void> {
		return this.$http.post(`/webapi/sso/${idp}/get-auth-data-check`,  { loginState })
			.then(this.handleResponse, this.handleError);
	}

	prepareAuthForm(config): Promise<PrepareAuthFormResponse> {
		return this.$http.get("/webapi/prepareauthform", config)
			.then(this.handleResponse, this.handleError);
	}

	prepareEmAuthForm(config): Promise<PrepareEmAuthFormResponse> {
		return this.$http.get("/webapi/prepareemauthform", config)
			.then(this.handleResponse, this.handleError);
	}

	createLoginState(formCollection): Promise<string> {
		let paramsToSend: string = "";
		Object.keys(formCollection).forEach((name: string) => {
			let value: string = formCollection[name];
			paramsToSend += '&' + encodeURIComponent(name) + "=" + encodeURIComponent(value);
		});
		const postData = paramsToSend.substring(1);

		let options = { headers: { "Content-type": "application/x-www-form-urlencoded; charset=utf-8" } };
		return this.$http.post("/webapi/auth/login-state", postData, options)
			.then(this.handleResponse, this.handleError);
	}

	getMobileInfo(loginState?: string): Promise<GetMobileInfoResponse> {
		return this.$http.get("/webapi/auth/mobile/info", { params: { loginState } })
			.then(this.handleResponse, this.handleError);
	}

	getEsiaPermissOptions(loginState?: string): Promise<EsiaPermissOptions> {
		return this.$http.get("/webapi/auth/esia-permiss-options", { params: { loginState } })
			.then(this.handleResponse, this.handleError);
	}

	prepareSsoData(ssoState: string): Promise<string> {
		return this.$http.post("/webapi/sso/prepare", null, { params: { ssoState } })
			.then(this.handleResponse);
	}

	ssoAlive(ssoState: string): Promise<void> {
		return this.$http.post("/webapi/sso/alive", null, { params: { ssoState } })
			.then(this.handleResponse);
	}
}